
import { isPlatformBrowser } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  Inject,
  OnInit,
  AfterViewInit,
  OnDestroy,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import {
  CurrentProductService,
  FocusConfig,
  ICON_TYPE,
  LaunchDialogService,
} from '@spartacus/storefront';
import { Subscription } from 'rxjs';

declare let View360: any;

@Component({
  selector: 'cx-product-image-zoom-dialog',
  templateUrl: 'product-360view-image-zoom-dialog.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class KbProduct360ViewImageZoomDialogComponent
  implements OnInit, AfterViewInit, OnDestroy {
  iconType = ICON_TYPE;

  /** Trap focus inside dialog, autofocus close button, Esc returns focus */
  focusConfig: FocusConfig = {
    trap: true,
    block: true,
    autofocus: 'button',
    focusOnEscape: true,
  };

  private subscription = new Subscription();
  private enteredFullscreen = false;
  private viewInstance: any | null = null; // keep a ref to View360 instance

  /** 360 container in template */
  @ViewChild('view360Ref', { static: false })
  view360Ref!: ElementRef<HTMLDivElement>;

  constructor(
    protected launchDialogService: LaunchDialogService,
    protected el: ElementRef<HTMLElement>,
    protected currentProductService: CurrentProductService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) { }

  /** ========== Utilities ========== */
  private supportsNativeFullscreen(): boolean {
    const doc: any = document;
    return !!(
      document.fullscreenEnabled ||
      doc.webkitFullscreenEnabled ||
      doc.msFullscreenEnabled
    );
  }

  private getFullscreenElement(): Element | null {
    const doc: any = document;
    return (
      doc.fullscreenElement ||
      doc.webkitFullscreenElement ||
      doc.msFullscreenElement ||
      null
    );
  }

  private applyPseudoFullscreen(): void {
    const dialogRoot = this.el.nativeElement.querySelector(
      '.cx-image-zoom-dialog'
    ) as HTMLElement | null;
    document.body.classList.add('no-scroll');
    dialogRoot?.classList.add('pseudo-fullscreen');
    this.enteredFullscreen = true;
    this.resizeToContainer();
  }

  /**
   * Try native fullscreen on target element, with proper Promise handling.
   * Fallback to pseudo-FS if rejected/unsupported.
   */
  private requestFullscreenOn(el: HTMLElement): void {
    const target: any = el;
    const req =
      target.requestFullscreen ||
      target.webkitRequestFullscreen ||
      target.msRequestFullscreen;

    if (!req) {
      this.applyPseudoFullscreen();
      return;
    }

    try {
      const p = req.call(target);
      if (p && typeof p.then === 'function') {
        p
          .then(() => {
            this.enteredFullscreen = true;
            this.resizeToContainer();
          })
          .catch((err: any) => {
            // Permissions check failed or not user-gesture: fallback
            console.warn('[KB360] Native fullscreen denied:', err);
            this.applyPseudoFullscreen();
          });
      } else {
        // Older engines that don't return a Promise
        this.enteredFullscreen = true;
        this.resizeToContainer();
      }
    } catch (err) {
      console.warn('[KB360] requestFullscreen threw:', err);
      this.applyPseudoFullscreen();
    }
  }

  private exitNativeFullscreen(): Promise<void> {
    const doc: any = document;
    const exit =
      doc.exitFullscreen || doc.webkitExitFullscreen || doc.msExitFullscreen;

    if (exit && this.getFullscreenElement()) {
      return Promise.resolve(exit.call(doc)).finally(() => {
        this.enteredFullscreen = false;
      });
    }
    this.enteredFullscreen = false;
    return Promise.resolve();
  }

  /** Public method you can call from PDP click handler right after launch */
  public requestFullscreenImmediately(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    const dialogRoot = this.el.nativeElement.querySelector(
      '.cx-image-zoom-dialog'
    ) as HTMLElement | null;

    // Lock background scroll
    document.body.classList.add('no-scroll');

    if (!this.supportsNativeFullscreen()) {
      this.applyPseudoFullscreen();
      return;
    }

    // Prefer scoping FS to the dialog element; fallback to <html>
    this.requestFullscreenOn(dialogRoot ?? document.documentElement);
  }

  /** Ask the viewer to re-measure after container size changes (enter FS/pseudo-FS) */
  private resizeToContainer(): void {
    try {
      if (this.viewInstance?.resize) {
        this.viewInstance.resize();
        return;
      }
      if (this.viewInstance?.refresh) {
        this.viewInstance.refresh();
        return;
      }
      // Fallback: restart on the same container
      this.viewInstance?.start?.(this.view360Ref.nativeElement);
    } catch (e) {
      console.warn('[KB360] Viewer resize/refresh failed:', e);
    }
  }

  /** ========== NEW: DOM-based initial zoom-out without cropping ========== */

  /** ========== NEW: DOM-based initial zoom-out + anti-cropping wrapper ========== */
  private applyInitialZoomOutDom(scale = 0.6): void {
    if (!isPlatformBrowser(this.platformId)) return;

    const host =
      this.el.nativeElement.querySelector('#view360') ||
      this.view360Ref?.nativeElement;
    if (!host) return;

    // (A) Make sure the immediate host never clips
    const hostEl = host as HTMLElement;
    hostEl.style.overflow = 'visible';

    // (B) Also try to neutralize common clippers the library may create
    // (names based on your earlier screenshots)
    const possibleClippers = [
      '.View360-holder',
      '.View360-canvasHolder',
      '.cx-dialog-body',
    ];
    possibleClippers.forEach(sel => {
      const el = hostEl.querySelector(sel) as HTMLElement | null;
      if (el) el.style.overflow = 'visible';
    });

    // Helper: find the canvas (or first child the viewer draws into)
    const findRenderable = (): HTMLElement | null => {
      const el =
        hostEl.querySelector('canvas') ||
        hostEl.querySelector('img') ||
        (hostEl.firstElementChild as HTMLElement | null);
      return el as HTMLElement | null;
    };

    // Wrap the renderable in an anti-cropping viewport (only once)
    const wrapRenderable = (renderable: HTMLElement) => {
      // If it's already wrapped, just apply scale and bail
      if (renderable.parentElement?.classList.contains('kb-zoom-viewport')) {
        renderable.style.transformOrigin = 'center center';
        renderable.style.transform = `scale(${scale})`;
        return;
      }

      // Create wrapper
      const vp = document.createElement('div');
      vp.className = 'kb-zoom-viewport';
      // The key bits that prevent cropping:
      vp.style.position = 'relative';
      vp.style.overflow = 'visible';
      vp.style.width = 'max-content';
      vp.style.height = 'max-content';
      // Center inside host
      vp.style.margin = 'auto';

      // Insert wrapper before renderable and move renderable inside it
      renderable.parentElement?.insertBefore(vp, renderable);
      vp.appendChild(renderable);

      // Apply initial scale on the renderable (not the wrapper),
      // so the wrapper can still expand later as the viewer zooms.
      renderable.style.transformOrigin = 'center center';
      renderable.style.transform = `scale(${scale})`;
      (renderable.style as any).imageRendering = 'auto';
    };

    const tryInit = () => {
      const renderable = findRenderable();
      if (!renderable) return false;
      wrapRenderable(renderable);
      return true;
    };

    if (tryInit()) return;

    // If the canvas hasn't been injected yet, observe and wrap when it appears
    const mo = new MutationObserver(() => {
      if (tryInit()) mo.disconnect();
    });
    mo.observe(hostEl, { childList: true, subtree: true });

    // Safety timeout
    setTimeout(() => mo.disconnect(), 5000);
  }


  /** ========== Lifecycle ========== */
  ngOnInit(): void {
    this.subscription.add(
      this.currentProductService.getProduct().subscribe((product) => {
        const view = new View360();
        this.viewInstance = view; // <-- keep a reference for resize/cleanup

        const product_code_360 = product['code']?.replace('/', '_');
        const rotationViewRootUrl =
          product['populateProductDetailForDisplayByCode'][
            'rotationViewRootUrl'
          ]?.replace(/\/$/, '');

        view.setImagesDirectory(rotationViewRootUrl);
        view.setFullSizeImagesDirectory(rotationViewRootUrl);
        view.setImagesPattern(product_code_360 + '_R0%ROW_C%CC%.jpg');

        const numberOfRows =
          product['populateProductDetailForDisplayByCode']['numberOfRows'];

        view.setMode('responsive-fit');

        // NOTE: We don't call any unknown "initial zoom" API here.
        // We'll apply initial zoom-out via DOM after .start(...)
        // view.setInitialZoom(50); // <-- remove/ignore if not supported

        view.setConfig({
          autoRotateDirection: -1,
          autoRotateSpeed: 250,
          rows: numberOfRows,
          columns: 24,
          fitmode: 'fit',
          firstImageOpacity: 1,
          height: 500,
          width: 500,
          imagesNumbering: 1,
          imagesNumberingUpDown: 1,
          loadOnPageLoad: true,
          initialImage: 1,
          opositeDirection: true,
          oneTurnOnStartUp: false,
          zoomMultipliers: [0.5, 0.7, 1.2, 1.5, 2, 3],
        });

        if (isPlatformBrowser(this.platformId)) {
          const host = document.getElementById('view360')!;
          view.start(host);
          view.setNavigationConfig({
            cornerRadius: 5,
            showFullscreen: false,
            showTurn: false,
          });


          // Initial "small" look
          requestAnimationFrame(() => this.applyInitialZoomOutDom(0.6));

          // ---- NEW: clear initial CSS scale on first user zoom ----
          // 1) Mouse wheel zoom
          host.addEventListener('wheel', () => this.clearInitialScaleOnce(), { passive: true });

          // 2) Touch pinch zoom (touchmove with 2 touches)
          host.addEventListener('touchmove', (e: TouchEvent) => {
            if (e.touches && e.touches.length === 2) this.clearInitialScaleOnce();
          }, { passive: true });

          // 3) Toolbar "Zoom In" button
          // The library renders a navigation holder; delegate a click
          const nav = host.querySelector('.View360-navigationHolder');
          nav?.addEventListener('click', (ev: Event) => {
            const t = ev.target as HTMLElement;
            // match on a data-attr/class the lib uses; fallback: any <button> inside nav
            if (t.closest('button') || t.closest('[title*="zoom"]') || t.closest('[class*="zoom"]')) {
              this.clearInitialScaleOnce();
            }
          });
          // Listen for fullscreenchange to keep state and auto-close if needed
          if (isPlatformBrowser(this.platformId)) {
            document.addEventListener('fullscreenchange', this.onFullscreenChange);
          }
        }
      }));
  }

  ngAfterViewInit(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    // Prevent background scroll immediately
    document.body.classList.add('no-scroll');

    /**
     * Try to enter fullscreen now. If the browser denies (common if not in click gesture),
     * we catch the rejection and fallback to pseudo fullscreen automatically.
     */
    const dialogRoot = this.el.nativeElement.querySelector(
      '.cx-image-zoom-dialog'
    ) as HTMLElement | null;

    if (!this.supportsNativeFullscreen()) {
      this.applyPseudoFullscreen();
    } else {
      this.requestFullscreenOn(dialogRoot ?? document.documentElement);
    }
  }

  ngOnDestroy(): void {
    // Remove listeners
    document.removeEventListener('fullscreenchange', this.onFullscreenChange);
    document.removeEventListener(
      'webkitfullscreenchange',
      this.onFullscreenChange as any
    );
    document.removeEventListener(
      'msfullscreenchange',
      this.onFullscreenChange as any
    );

    // Exit FS/pseudo FS & unlock scroll
    this.exitFullscreenIfActive().finally(() => {
      document.body.classList.remove('no-scroll');
    });

    this.subscription.unsubscribe();
    this.viewInstance = null;
  }

  /** ========== Close + Esc ========== */

  close(reason = ''): void {
    // Exit fullscreen first, then close the dialog
    this.exitFullscreenIfActive().finally(() => {
      document.body.classList.remove('no-scroll');
      this.launchDialogService.closeDialog(reason);
    });
  }

  private exitFullscreenIfActive(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) return Promise.resolve();

    const dialogRoot = this.el.nativeElement.querySelector(
      '.cx-image-zoom-dialog'
    ) as HTMLElement | null;

    if (!this.supportsNativeFullscreen()) {
      dialogRoot?.classList.remove('pseudo-fullscreen');
      this.enteredFullscreen = false;
      return Promise.resolve();
    }

    return this.exitNativeFullscreen();
  }

  @HostListener('document:keydown.escape')
  onEsc(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    if (this.getFullscreenElement()) {
      // Native FS: exit; dialog will close via fullscreenchange handler
      this.exitNativeFullscreen().catch(() => { });
    } else {
      // Pseudo or no FS: close directly
      this.close('escape');
    }
  }

  /** Resize on entry; auto-close on exit of native FS for consistent UX */
  private onFullscreenChange = (): void => {
    const inFs = !!this.getFullscreenElement();

    if (inFs) {
      this.enteredFullscreen = true;
      this.resizeToContainer();
    } else if (this.enteredFullscreen) {
      // Leaving native fullscreen: cleanup and close dialog
      const dialogRoot = this.el.nativeElement.querySelector(
        '.cx-image-zoom-dialog'
      ) as HTMLElement | null;
      dialogRoot?.classList.remove('pseudo-fullscreen');

      this.enteredFullscreen = false;
      document.body.classList.remove('no-scroll');
      this.launchDialogService.closeDialog('fullscreen exit');
    }
  };

  private clearInitialScaleOnce = (() => {
    let cleared = false;
    return () => {
      if (cleared) return;
      const host = document.getElementById('view360');
      const renderable = host?.querySelector(
        '.kb-zoom-viewport > canvas, .kb-zoom-viewport > img'
      ) as HTMLElement | null;
      if (renderable) {
        renderable.style.transform = 'none';
        cleared = true;
        // Keep the anti-crop wrapper so nothing clips when the library zooms
      }
    };
  })();
}
